import { useRef } from 'react';
import { useAtomValue } from 'jotai';
import { useInterval } from 'usehooks-ts';
import { sessionStartHeapAtom } from '../../atoms/session-atoms';
import { HEAP_GAUGE_INTERVAL_MS, PERFORMANCE_METRIC_EVENTS } from '../../constants/performance';
import { getHeapTags } from '../../utils/performance-tag-helpers';
import { usePerformanceMetrics } from './use-performance-metrics';

/**
 * Periodically emits browser_heap_used_mb and browser_heap_growth_mb gauges.
 * Chrome-only; no-op when performance.memory is unavailable.
 * Mount once at studio root level via PerformanceInitializer.
 */
export function useHeapGauge() {
  const {
    captureGauge
  } = usePerformanceMetrics();
  const captureGaugeRef = useRef(captureGauge);
  // eslint-disable-next-line react-hooks/refs
  captureGaugeRef.current = captureGauge;
  const sessionStartHeap = useAtomValue(sessionStartHeapAtom);

  // Check if performance.memory is available (Chrome-only)
  const isMemoryAvailable = getHeapTags().browserHeapTotal > 0;
  useInterval(() => {
    const heapTags = getHeapTags();
    if (heapTags.browserHeapUsed === 0) return;
    captureGaugeRef.current({
      name: PERFORMANCE_METRIC_EVENTS.BROWSER_HEAP_USED_MB,
      value: heapTags.browserHeapUsed
    });
    if (sessionStartHeap > 0) {
      captureGaugeRef.current({
        name: PERFORMANCE_METRIC_EVENTS.BROWSER_HEAP_GROWTH_MB,
        value: Math.max(0, heapTags.browserHeapUsed - sessionStartHeap)
      });
    }
  }, isMemoryAvailable ? HEAP_GAUGE_INTERVAL_MS : null);
}